/**
 * Centralized query key factory.
 * Always reference keys through this object so cache invalidation is predictable.
 *
 *   queryClient.invalidateQueries({ queryKey: qk.events.all })
 *   queryClient.invalidateQueries({ queryKey: qk.events.detail(eventId) })
 */
export const qk = {
  user: {
    me: ["user", "me"] as const,
  },
  company: {
    current: (companyId: string) => ["company", companyId] as const,
  },
  events: {
    all: ["events"] as const,
    list: (companyId: string, fields: string) =>
      ["events", "list", companyId, fields] as const,
    recent: (recentIds: string[]) =>
      ["events", "recent", ...recentIds] as const,
    detail: (eventId: string, fields?: string) =>
      ["events", "detail", eventId, fields ?? "all"] as const,
    // Guest contingent of the event's OWNING company. Keyed by eventId, not by
    // companyId: the whole point is that the caller's company is the wrong
    // tenant here, so a company-keyed entry would be a lie about what is
    // cached. invalidateEventScoped matches it via the eventId element.
    guestQuota: (eventId: string) => ["events", "guest-quota", eventId] as const,
    // Plan features / mail quota / AI credits / company role of the event's
    // OWNING company — same reasoning as guestQuota. The prefix is exported on
    // its own because the plan-purchase paths and the AI credit ledger
    // invalidate every event's entry at once (a new plan or a settled AI turn
    // changes the answer for all of them).
    entitlementsPrefix: ["events", "entitlements"] as const,
    entitlements: (eventId: string) => ["events", "entitlements", eventId] as const,
    // The verified SMS numbers of the event's OWNING company, masked — what
    // the SEND_SMS editor's fixed-recipient picker offers (SMS channel wave).
    smsNumbers: (eventId: string) => ["events", "sms-numbers", eventId] as const,
    // The event's SMS log, paged by cursor (SMS plugin wave, BE-5).
    smsLog: (eventId: string) => ["events", "sms-log", eventId] as const,
    // One manual send's rows, skips and counts (SMS manual send, F-4) — under
    // the log's prefix, so every invalidation of the log reaches the per-send views.
    smsSendView: (eventId: string, jobId: string) => ["events", "sms-log", eventId, "send", jobId] as const,
    // The SMS send dialog's recipient check (B-2). `openId` is minted per
    // dialog open: the answer is never shared between opens or tabs and the
    // query keeps no cache (staleTime 0, gcTime 0; plan F3-4).
    smsManualCheck: (eventId: string, openId: string) => ["events", "sms-manual-check", eventId, openId] as const,
    // The event's SMS templates with their usage counts (SMS templates wave,
    // F-1): the templates page, the SEND_SMS editor's picker, the list's
    // broken badge. Invalidated on every template write and on every workflow
    // create / update / delete (the usage counts move).
    smsTemplates: (eventId: string) => ["events", "sms-templates", eventId] as const,
  },
  dashboards: {
    forEvent: (eventId: string) => ["dashboards", eventId] as const,
  },
  automation: {
    // The automation page's own event read (name, archived, plugins, zone,
    // dates) — the source of the editors' `smsPluginOn`. Keeps the page's
    // ["eventAutomationWorkflows", eventId] prefix, which every workflow save
    // invalidates whole; the SMS editor re-reads it alone after the "switch SMS
    // on" new-tab link and after a save refused with `smsPluginDisabled`.
    event: (eventId: string) => ["eventAutomationWorkflows", eventId, "event"] as const,
  },
  eventSettings: {
    // The pending-transfer status (GET /event-roles/{e}/transfer-invitation):
    // read by the members banner and the ownership card, invalidated by the
    // cancel and the cross-company initiate — one key, never a copy (audit r1
    // F4-2: a drifted copy left the banner up after a cancel). It keeps the
    // members pages' prefix ["eventSettings", eventId, "collab"], which the
    // event invite invalidates whole.
    transferStatus: (eventId: string) => ["eventSettings", eventId, "collab", "transfer"] as const,
    // Arrival-notice contacts (W4): the settings page's list and one guest's
    // own / inherited links (the guest popup). Both sit under the prefix
    // ["eventSettings", eventId, "notifications"], which a link or unlink
    // invalidates whole (the page's guestCount moves with it).
    notificationsPrefix: (eventId: string) => ["eventSettings", eventId, "notifications"] as const,
    notificationContacts: (eventId: string) => ["eventSettings", eventId, "notifications", "contacts"] as const,
    // Every guest's lookup of one event (audit r1 A5-11: a settings-page
    // change or a finished import marks them stale without touching the
    // settings page's own event / role reads under the same prefix).
    guestNotificationContactsPrefix: (eventId: string) =>
      ["eventSettings", eventId, "notifications", "guest"] as const,
    guestNotificationContacts: (eventId: string, guestId: string) =>
      ["eventSettings", eventId, "notifications", "guest", guestId] as const,
    // plan arrival-automation Rev. 4 §1.4: the NOTIFY_CONTACT editor's picker
    // (under the same prefix, so every registry change marks it stale too).
    notificationContactPicker: (eventId: string) =>
      ["eventSettings", eventId, "notifications", "picker"] as const,
  },
  setupPlan: {
    doc: (eventId: string) => ["setup-plan", "doc", eventId] as const,
  },
  eventGoals: {
    doc: (eventId: string) => ["event-goals", "doc", eventId] as const,
    kpiValues: (eventId: string) => ["event-goals", "kpi-values", eventId] as const,
    kpiHistory: (eventId: string, days: number) =>
      ["event-goals", "kpi-history", eventId, days] as const,
    kpiHistoryAll: (eventId: string) => ["event-goals", "kpi-history", eventId] as const,
    analysisJob: (eventId: string, jobId: string) =>
      ["event-goals", "analysis-job", eventId, jobId] as const,
    cohortValues: (eventId: string, dimension: string) =>
      ["event-goals", "cohort-values", eventId, dimension] as const,
  },
  notifications: {
    page: (page: number, size: number, olderThanIso: string, read?: boolean) =>
      [
        "notifications",
        page,
        size,
        olderThanIso,
        read === undefined ? "any" : read ? "read" : "unread",
      ] as const,
    unreadCount: ["notifications", "unread-count"] as const,
  },
  slotting: {
    offerings: (eventId: string) => ["slotting", "offerings", eventId] as const,
    // NOTE: per-offering keys (offering/lanes/treatments/appointments/requests)
    // deliberately do NOT live here. The offering page queries ad-hoc
    // ["slotting", eventId, offeringId, …] keys, and react-query filters match
    // key elements positionally — a qk factory with the type name at index 1
    // could never match them (a whole dead hook family built on such keys was
    // removed). Invalidate via lib/slotting/invalidateSlotting.ts instead.
  },
} as const;
